import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useMirrorAdminAllCompaniesQuery,
  useOrderMirrorCaptureConfigQuery,
  useSetOrderMirrorCaptureConfigMutation,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";

const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxWidth: 640,
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    p: 2,
  } satisfies SxProps<Theme>,
};

type CompanyOption = { uuid: string; name: string };

type CaptureMode = "NONE" | "ALL" | "COMPANIES";

/**
 * Production-side panel controlling which newly-created orders are written to
 * the mirror outbox: nothing (NONE), every company (ALL), or a chosen list
 * (COMPANIES). This is the inflow control — independent of which environments
 * receive the captured orders (that is the targets panel).
 */
const MirrorCapturePanel = () => {
  const { data, error, refetch } = useOrderMirrorCaptureConfigQuery({
    fetchPolicy: "network-only",
  });
  const { data: companiesData } = useMirrorAdminAllCompaniesQuery();
  const [setCaptureConfig, { loading: saving }] =
    useSetOrderMirrorCaptureConfigMutation();

  const [mode, setMode] = useState<CaptureMode>("NONE");
  const [selected, setSelected] = useState<CompanyOption[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data !== undefined) {
      setMode(data.orderMirrorCaptureConfig.mode as CaptureMode);
      setSelected(
        data.orderMirrorCaptureConfig.companies.map((c) => ({
          uuid: c.companyUuid,
          name: c.companyName,
        })),
      );
    }
  }, [data]);

  const companyOptions = useMemo(
    () =>
      (companiesData?.companies ?? []).map((c) => ({
        uuid: c.uuid,
        name: c.name,
      })),
    [companiesData],
  );

  const save = useCallback(async () => {
    setSaveError(null);
    setSaved(false);
    if (mode === "COMPANIES" && selected.length === 0) {
      setSaveError("Select at least one company, or choose a different mode.");
      return;
    }
    try {
      const result = await setCaptureConfig({
        variables: {
          input: {
            mode,
            companyUuids:
              mode === "COMPANIES" ? selected.map((c) => c.uuid) : null,
          },
        },
      });
      throwOnGraphqlErrors(result);
      setSaved(true);
      await refetch();
    } catch (mutationError) {
      setSaveError(
        mutationError instanceof Error ? mutationError.message : "Save failed",
      );
    }
  }, [mode, refetch, selected, setCaptureConfig]);

  return (
    <Box sx={styles.panel}>
      <Typography variant="h6">Outbox capture</Typography>
      <Typography variant="body2">
        Controls which newly-created orders are saved for mirroring. Targets
        below control which environments receive them.
      </Typography>
      {error !== undefined && <Alert severity="error">{error.message}</Alert>}
      {saveError !== null && <Alert severity="error">{saveError}</Alert>}
      {saved && <Alert severity="success">Capture policy saved.</Alert>}
      <RadioGroup
        value={mode}
        onChange={(event) => {
          setMode(event.target.value as CaptureMode);
          setSaved(false);
        }}
      >
        <FormControlLabel
          control={<Radio />}
          label="Off — capture nothing"
          value="NONE"
        />
        <FormControlLabel
          control={<Radio />}
          label="All companies"
          value="ALL"
        />
        <FormControlLabel
          control={<Radio />}
          label="Selected companies"
          value="COMPANIES"
        />
      </RadioGroup>
      {mode === "COMPANIES" && (
        <Autocomplete
          multiple
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
          options={companyOptions}
          renderInput={(params) => (
            <TextField {...params} label="Companies to capture" />
          )}
          size="small"
          value={selected}
          onChange={(_, value) => {
            setSelected(value);
            setSaved(false);
          }}
        />
      )}
      <Stack direction="row" spacing={1}>
        <Button
          disabled={saving}
          variant="contained"
          onClick={() => {
            void save();
          }}
        >
          Save capture policy
        </Button>
      </Stack>
    </Box>
  );
};

export default MirrorCapturePanel;
